/**
 * useCustomerVehicles - Veículos vinculados a cliente / contato / negócio
 *
 * Toda leitura e escrita passa por RPCs SECURITY DEFINER (nunca SELECT direto).
 *
 * @version 1.4.0
 * RELEASE: 2026-08-24 v1.4.0 - Referência estável para a lista vazia (corrige loop de efeito
 *            que limpava a seleção de veículos no modal de Novo Negócio)
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useCustomerVehicles');

/** Tipos de veículo suportados */
export type VehicleType = 'CAR' | 'MOTORCYCLE' | 'TRUCK' | 'OTHER';

/** Rótulos em português dos tipos de veículo */
export const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  CAR: 'Carro',
  MOTORCYCLE: 'Moto',
  TRUCK: 'Caminhão',
  OTHER: 'Outros',
};

/** Veículo retornado pela RPC list_customer_vehicles */
export interface CustomerVehicle {
  id: string;
  vehicle_type: VehicleType;
  category_id: string | null;
  category_name: string | null;
  brand_id: string | null;
  brand_name: string | null;
  model_id: string | null;
  model_name: string | null;
  year_code: string | null;
  year: number | null;
  fuel: string | null;
  color: string | null;
  plate: string | null;
  renavam: string | null;
  chassis: string | null;
  fipe_code: string | null;
  fipe_value: number | null;
  notes: string | null;
  customer_id: string | null;
  contact_id: string | null;
  /** v1.1.0 — total de multas em aberto (sincronizadas) */
  fines_total_open?: number | null;
  /** v1.1.0 — data da última sincronização de multas */
  fines_synced_at?: string | null;
  /** 2026-10-02 — status do veículo (catálogo vehicle_statuses) */
  status_id?: string | null;
  created_at: string;
}

/** Filtro de escopo da listagem */
export interface VehicleScope {
  customerId?: string | null;
  contactId?: string | null;
  dealId?: string | null;
  /** v1.3.0 — veículos vinculados a uma tarefa (task_vehicles) */
  taskId?: string | null;
}

/** Dados aceitos pelo formulário de veículo */
export interface VehicleInput extends VehicleScope {
  id?: string | null;
  vehicle_type: VehicleType;
  category_id?: string | null;
  brand_id?: string | null;
  brand_name?: string | null;
  model_id?: string | null;
  model_name?: string | null;
  year_code?: string | null;
  year?: number | null;
  fuel?: string | null;
  color?: string | null;
  plate?: string | null;
  renavam?: string | null;
  chassis?: string | null;
  fipe_code?: string | null;
  fipe_value?: number | null;
  notes?: string | null;
  /** 2026-10-02 — status; nulo mantém o atual (edição) ou usa Ativo (novo) */
  status_id?: string | null;
}

/**
 * Lista vazia compartilhada (v1.4.0).
 *
 * Antes o hook devolvia `query.data ?? []`, criando um NOVO array a cada render
 * quando a query estava desabilitada. Consumidores que usam a lista como
 * dependência de useEffect entravam em loop de atualização (ex.: os chips de
 * veículos do Novo Negócio eram limpos logo após a seleção).
 */
const EMPTY_VEHICLES: CustomerVehicle[] = [];

/**
 * Lista e mantém os veículos de um cliente, contato ou negócio.
 */
export function useCustomerVehicles(scope: VehicleScope, enabled = true) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  const hasScope = !!(scope.customerId || scope.contactId || scope.dealId || scope.taskId);

  const query = useQuery({
    queryKey: [
      'customer-vehicles',
      tenantId,
      scope.customerId,
      scope.contactId,
      scope.dealId,
      scope.taskId,
    ],
    enabled: enabled && hasScope && !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<CustomerVehicle[]> => {
      const { data, error } = await (supabase.rpc as any)('list_customer_vehicles', {
        p_customer_id: scope.customerId ?? null,
        p_contact_id: scope.contactId ?? null,
        p_deal_id: scope.dealId ?? null,
        p_task_id: scope.taskId ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar veículos', { error: error.message });
        throw error;
      }
      return (data || []) as CustomerVehicle[];
    },
  });

  /** Invalida a listagem do escopo atual e os contadores do modal */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['customer-vehicles'] });
    // v1.1.0: mantém o badge da aba Veículos sincronizado com a listagem
    queryClient.invalidateQueries({ queryKey: ['customer-modal-data'] });
    // v1.2.0: mantém a página "Lista Veículos" e seus KPIs sincronizados
    queryClient.invalidateQueries({ queryKey: ['tenant-vehicles'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-vehicles-kpis'] });
  };


  /** Cria ou edita um veículo */
  const saveVehicle = useMutation({
    mutationFn: async (input: VehicleInput) => {
      const { data, error } = await (supabase.rpc as any)('upsert_customer_vehicle', {
        p_vehicle_type: input.vehicle_type,
        p_vehicle_id: input.id ?? null,
        p_customer_id: input.customerId ?? scope.customerId ?? null,
        p_contact_id: input.contactId ?? scope.contactId ?? null,
        p_category_id: input.category_id ?? null,
        p_brand_id: input.brand_id ?? null,
        p_brand_name: input.brand_name ?? null,
        p_model_id: input.model_id ?? null,
        p_model_name: input.model_name ?? null,
        p_year_code: input.year_code ?? null,
        p_year: input.year ?? null,
        p_fuel: input.fuel ?? null,
        p_color: input.color ?? null,
        p_plate: input.plate ?? null,
        p_renavam: input.renavam ?? null,
        p_chassis: input.chassis ?? null,
        p_fipe_code: input.fipe_code ?? null,
        p_fipe_value: input.fipe_value ?? null,
        p_notes: input.notes ?? null,
        p_status_id: input.status_id ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Veículo salvo');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  /** Exclui (logicamente) um veículo */
  const deleteVehicle = useMutation({
    mutationFn: async (vehicleId: string) => {
      const { error } = await (supabase.rpc as any)('delete_customer_vehicle', {
        p_vehicle_id: vehicleId,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Veículo excluído');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return {
    // v1.4.0: referência estável quando não há dados (evita efeitos/memos disparando a cada render)
    vehicles: query.data ?? EMPTY_VEHICLES,
    isLoading: query.isLoading,
    refetch: query.refetch,
    saveVehicle,
    deleteVehicle,
  };
}

/**
 * Vincula/desvincula um veículo de um negócio.
 */
export function useDealVehicleLink() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { dealId: string; vehicleId: string; unlink?: boolean }) => {
      const { error } = await (supabase.rpc as any)('link_deal_vehicle', {
        p_deal_id: input.dealId,
        p_vehicle_id: input.vehicleId,
        p_unlink: input.unlink ?? false,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-vehicles'] });
      // v1.5.0: atualiza o contador da aba "Veículos" no modal de negócio
      queryClient.invalidateQueries({ queryKey: ['deal-modal-data'] });
      // v1.4.0: seção "Vínculos" do modal de edição de veículo
      queryClient.invalidateQueries({ queryKey: ['vehicle-details'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * Vincula/desvincula um veículo de uma tarefa (task_vehicles).
 *
 * v1.3.0 — permite múltiplos veículos por tarefa. A RPC mantém a coluna
 * legada `tasks.vehicle_id` sincronizada com o primeiro vínculo.
 */
export function useTaskVehicleLink() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { taskId: string; vehicleId: string; unlink?: boolean }) => {
      const { error } = await (supabase.rpc as any)('link_task_vehicle', {
        p_task_id: input.taskId,
        p_vehicle_id: input.vehicleId,
        p_unlink: input.unlink ?? false,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-vehicles'] });
      // Mantém contadores/listagens da tarefa e do veículo sincronizados
      queryClient.invalidateQueries({ queryKey: ['task-details'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-activity-log'] });
      // v1.4.0: seção "Vínculos" do modal de edição de veículo
      queryClient.invalidateQueries({ queryKey: ['vehicle-details'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
}



/**
 * Vincula os veículos selecionados a um contrato (substitui a seleção anterior).
 */
export function useContractVehicles(contractId: string | null, enabled = true) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['contract-vehicles', contractId],
    enabled: enabled && !!contractId && !!currentTenant?.id,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('list_contract_vehicles', {
        p_contract_id: contractId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
      return (data || []) as Array<Record<string, any>>;
    },
  });

  const linkVehicles = useMutation({
    mutationFn: async (input: { contractId: string; vehicleIds: string[] }) => {
      const { error } = await (supabase.rpc as any)('link_vehicles_to_contract', {
        p_contract_id: input.contractId,
        p_vehicle_ids: input.vehicleIds,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-vehicles'] });
    },
  });

  return { vehicles: query.data ?? [], isLoading: query.isLoading, linkVehicles };
}
